/**
 * Paragraph - Simple paragraph component for body text
 * Simplifies content editing by providing easy-to-understand props
 */

import React from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface ParagraphProps {
	children: React.ReactNode
	size?: 'small' | 'medium' | 'large'
	color?: 'primary' | 'secondary' | 'white' | 'inherit'
	align?: 'left' | 'center' | 'right'
	weight?: 'normal' | 'medium' | 'bold'
}

const Paragraph: React.FC<ParagraphProps> = ({ children, size = 'medium', color = 'inherit', align = 'left', weight = 'normal' }) => {
	const theme = useTheme()

	const getVariant = () => {
		switch (size) {
			case 'small':
				return 'body2'
			case 'medium':
				return 'body1'
			case 'large':
				return 'h6'
			default:
				return 'body1'
		}
	}

	const getColor = () => {
		switch (color) {
			case 'primary':
				return theme.palette.primary.main
			case 'secondary':
				return theme.palette.secondary.main
			case 'white':
				return 'white'
			case 'inherit':
				return 'inherit'
			default:
				return 'inherit'
		}
	}

	const getFontWeight = () => {
		switch (weight) {
			case 'normal':
				return 400
			case 'medium':
				return 500
			case 'bold':
				return 600
			default:
				return 400
		}
	}

	return (
		<Typography
			variant={getVariant()}
			component="p"
			sx={{
				color: getColor(),
				textAlign: align,
				fontWeight: getFontWeight(),
				lineHeight: 1.6
			}}
		>
			{children}
		</Typography>
	)
}

export default Paragraph
